import {
  AddOnId,
  AIO_BUNDLE_ADD_ON_IDS,
  AIO_BUNDLE_DISCOUNT_RATE,
  AIO_EXISTING_USERS_PROMO_RATE,
  SubscriptionPlan,
} from '@apps-billing-shared/definitions';
import { usePlansStore } from '@apps-billing-shared/store/plans';
import { usePricingStore } from '@apps-billing-shared/store/pricing';
import { SUBSCRIPTION } from '@apps-once/definitions';
import { getAioExistingUserPromoBillingTerm } from '@apps-once/composables/use-aio-existing-user';
import { useOnceStore } from '@apps-once/store';
import { useAddOnStore } from '@apps-once/store/add-on';
import { useCartStore } from '@apps-once/store/cart';
import { useCheckoutStore } from '@apps-once/store/checkout';
import { isAioBundleRetained } from '@apps-once/util/aio-bundle-retention';
import mushodoPromise from '@webapp-next/lib/request/webapp/mushodo-promise';
import { DateTime } from 'luxon';
import { defineStore } from 'pinia';

export interface PromoCodeDiscount {
  code: string;
  description: string;
  planId?: SubscriptionPlan;
  addOnId?: AddOnId;
  percentage: number;
  amount: number;
  freeMonths: number;
}

interface AnnualUpgradePricing {
  proratedAmount: number;
  priceIncTax: number;
  previousPlanCredit: number;
  netChargeToday: number;
  currency: string;
  addTax: boolean;
}

// One representative monthly instalment from the AIO
// apply-bundle preview on an annual-paid-monthly plan.
interface AioApmInstalmentPricing {
  price: number;
  priceIncTax: number;
}

interface QuoteStoreState {
  total: number;
  promoCodeError: string;
  promoDiscounts: Array<SubscriptionPlan | AddOnId>;
  promoDiscountMap: Record<SubscriptionPlan | AddOnId, PromoCodeDiscount>;
  // Uppercased codes that were applied automatically (not user-typed). Used to
  // tag analytics events; intersected with the live discounts at read time so
  // stale entries (e.g. a removed add-on's code) do not count.
  autoAppliedCodes: string[];
  annualUpgradePricing: AnnualUpgradePricing | null;
  isLoadingAnnualUpgradePricing: boolean;
  annualUpgradePricingError: string;
  aioApmInstalmentPricing: AioApmInstalmentPricing | null;
  // Bumped by every fetch and reset, so a preview that lands after the tenant
  // moved on (or a newer request started) writes nothing.
  annualUpgradePricingRequestId: number;
}

const TRIAL_LENGTH_DAYS = 32;

export const useQuoteStore = defineStore('once:quote ', {
  state(): QuoteStoreState {
    return {
      total: 0,
      promoCodeError: '',
      promoDiscounts: [],
      promoDiscountMap: {},
      autoAppliedCodes: [],
      annualUpgradePricing: null,
      isLoadingAnnualUpgradePricing: false,
      annualUpgradePricingError: '',
      aioApmInstalmentPricing: null,
      annualUpgradePricingRequestId: 0,
    };
  },
  getters: {
    isAioBundleRetained(): boolean {
      return isAioBundleRetained({
        once: useOnceStore(),
        cart: useCartStore(),
        checkout: useCheckoutStore(),
        plans: usePlansStore(),
      });
    },
    // A kept bundle's HR and Payroll are unchanged, so they are left out of the priced add-ons.
    pricedAnnualAddOns(): Array<AddOnId> {
      const annualAddOns = useCartStore().annualAddOns;
      return this.isAioBundleRetained
        ? annualAddOns.filter(id => !AIO_BUNDLE_ADD_ON_IDS.includes(id))
        : annualAddOns;
    },
    pricedMonthlyAddOns(): Array<AddOnId> {
      const monthlyAddOns = useCartStore().monthlyAddOns;
      return this.isAioBundleRetained
        ? monthlyAddOns.filter(id => !AIO_BUNDLE_ADD_ON_IDS.includes(id))
        : monthlyAddOns;
    },
    promoCodeDiscounts(): Array<PromoCodeDiscount> {
      return this.promoDiscounts.map(
        (discount: SubscriptionPlan | AddOnId) =>
          this.promoDiscountMap[discount]
      );
    },
    activeUserCount(): number {
      return useOnceStore().totalEmployeeCount;
    },
    monthlyAddOnsTotalExtTax(): number {
      const userCount = this.activeUserCount;
      const addOns = this.pricedMonthlyAddOns;
      const planId = useCartStore().plan;
      const weeklyMultiplier = useCartStore().hasActiveUserBilling ? 4 : 1;
      return addOns.reduce((total, addOnId) => {
        const addOnCost =
          useOnceStore().getAddOnPrice(addOnId, planId) *
          userCount *
          weeklyMultiplier;

        return (
          total +
          addOnCost -
          useQuoteStore().addOnPromoDiscountAmount(addOnCost, addOnId, false)
        );
      }, 0);
    },
    monthlyAddOnsTotalAfterDiscount(): number {
      // apply discounts here
      return this.monthlyAddOnsTotalExtTax;
    },
    annualAddOnsTotal(): number {
      const annualAddOns = this.pricedAnnualAddOns;

      if (!annualAddOns.length) {
        return 0;
      }

      const total = annualAddOns.reduce((totalPrice, addOnId) => {
        const addOnPrice = useQuoteStore().getAddOnPrice(addOnId);
        return totalPrice + addOnPrice;
      }, 0);

      return total;
    },
    annualAddOnsTotalAfterDiscount(): number {
      const annualAddOns = this.pricedAnnualAddOns;

      if (!annualAddOns.length) {
        return 0;
      }

      const total = annualAddOns.reduce((totalDiscount, addOnId) => {
        const addOnPrice = useQuoteStore().getAddOnPrice(addOnId);
        const addOnDiscount = useQuoteStore().addOnPromoDiscountAmount(
          addOnPrice,
          addOnId,
          true
        );
        return totalDiscount + addOnPrice - addOnDiscount;
      }, 0);

      // Net the add-ons' bundle share only when the base plan is annual (mirror
      // of the monthly guard) — an annual non-bundle add-on under a monthly
      // bundle must not absorb the bundle's saving. 0 outside the AIO flow.
      const addOnsBundle = useCartStore().isAnnual
        ? this.addOnsBundleDiscount
        : 0;
      return Math.max(0, total - addOnsBundle);
    },
    planWithActiveUserPrice(): number {
      return useCartStore().planPrice * this.activeUserCount;
    },
    planWithAnnualeUserPrice(): number {
      return useCartStore().planPrice * useCartStore().annualUserCount;
    },
    planMonthlyPrice(): number {
      if (this.minimumPlanSpendRemaining > 0) {
        return this.minimumPlanSpend;
      }
      return this.planWithActiveUserPrice;
    },
    monthlyTotalExtTax(): number {
      const addOnsTotal = this.monthlyAddOnsTotalAfterDiscount;

      // If on AddOn purchase step, simply return the addOnsTotal
      if (useCheckoutStore().isAddOnPurchaseStep) {
        return addOnsTotal;
      }

      // Else, include the plan Total
      const planTotal = useCartStore().isMonthly
        ? this.planWithActiveUserPrice
        : 0;

      // Include the minimumPlanSpendRemaining if it exists (only applicable when on the plan checkout)
      return this.minimumPlanSpendRemaining > 0 &&
        useCheckoutStore().isSelectingPlanType
        ? addOnsTotal + planTotal + this.minimumPlanSpendRemaining
        : addOnsTotal + planTotal;
    },
    // The plan's own monthly charge, with no add-on component. A promo code
    // covers a single product, so a plan-scoped code discounts the plan alone —
    // add-ons are discounted only by a code issued against that add-on.
    monthlyPlanExtTax(): number {
      // No plan is being purchased on the add-on step, so a plan code has
      // nothing to discount.
      if (useCheckoutStore().isAddOnPurchaseStep) {
        return 0;
      }

      // Under an annual/APM base the monthly section holds add-ons alone, which
      // a plan code must not touch. The minimum spend is a floor on the monthly
      // plan charge, so it is only part of this base when the plan is billed
      // monthly — otherwise it would leak a plan discount onto those add-ons.
      if (!useCartStore().isMonthly) {
        return 0;
      }

      // Include the minimumPlanSpendRemaining if it exists (only applicable when on the plan checkout)
      return this.minimumPlanSpendRemaining > 0 &&
        useCheckoutStore().isSelectingPlanType
        ? this.planWithActiveUserPrice + this.minimumPlanSpendRemaining
        : this.planWithActiveUserPrice;
    },
    monthlyPromoDiscountAmount(): number {
      return useQuoteStore()._promoDiscountAmount(
        this.monthlyPlanExtTax,
        false
      );
    },
    monthlyTotalAfterDiscount(): number {
      if (this.monthlyPromoDiscountAmount < 0) {
        return 0;
      }
      // The bundle lives on the base plan's schedule, so only net it into the
      // monthly total when that schedule is monthly. With an annual/APM base the
      // monthly section holds only non-bundle monthly add-ons (Messaging+ /
      // Analytics+), and the annual bundle saving must not cancel them out
      // (GROWTH-3472). bundleSavingsTotal is 0 outside the AIO flow.
      const bundleSaving = useCartStore().isMonthly
        ? this.bundleSavingsTotal
        : 0;
      return Math.max(
        0,
        this.monthlyTotalExtTax - this.monthlyPromoDiscountAmount - bundleSaving
      );
    },
    monthlyTaxCost(): number {
      return this.monthlyTotalAfterDiscount * this.taxRate;
    },
    monthlyTotalIncTax(): number {
      return this.monthlyTaxCost + this.monthlyTotalAfterDiscount;
    },
    annualTotalExtTax(): number {
      const planTotal = useCartStore().isAnnual
        ? this.planWithAnnualeUserPrice
        : 0;

      return planTotal * 12;
    },
    // annualTotalExtTax is the plan charge alone, which is the whole base a
    // plan-scoped code may discount — annual add-ons carry their own codes.
    annualPromoDiscountAmount(): number {
      return useQuoteStore()._promoDiscountAmount(this.annualTotalExtTax, true);
    },
    annualTotalAfterDiscount(): number {
      if (this.annualPromoDiscountAmount < 0) {
        return 0;
      }
      // Mirror of the monthly guard: only net the plan's bundle share when the
      // base plan is on an annual schedule (GROWTH-3472). planBundleDiscount is 0
      // outside the AIO flow, so standard checkout is unaffected.
      const planBundle = useCartStore().isAnnual ? this.planBundleDiscount : 0;
      return Math.max(
        0,
        this.annualTotalExtTax - this.annualPromoDiscountAmount - planBundle
      );
    },
    annualTaxCost(): number {
      return this.annualTotalAfterDiscount * this.taxRate;
    },
    annualTotalAfterDiscountIncTax(): number {
      return this.annualTaxCost + this.annualTotalAfterDiscount;
    },
    annualSavingsTotal(): number {
      if (!useCartStore().isAnnual) {
        return 0;
      }

      if (useCheckoutStore().isAddOnPurchaseStep) {
        const userCount = useOnceStore().getPrePurchasedUserCountFromPlan();

        return useOnceStore().getAnnualAddOnDiscountAmount(
          String(useCartStore().plan),
          useAddOnStore().addOnId,
          userCount
        );
      }

      const annualDiscountAmount = useOnceStore().getAnnualDiscountAmount(
        useCartStore().plan,
        useCartStore().annualUserCount
      );

      // getAnnualDiscountAmount returns a .toFixed(2) string; coerce so callers
      // (e.g. bundleHeadlineSaving) add numerically instead of concatenating.
      if (this.annualAddOnDiscountAmount > 0) {
        return Number(annualDiscountAmount) + this.annualAddOnDiscountAmount;
      } else {
        return Number(annualDiscountAmount);
      }
    },
    annualAddOnDiscountAmount(): number {
      return this.pricedAnnualAddOns.reduce(
        (totalDiscount, addOnId: string) => {
          totalDiscount += useOnceStore().getAnnualAddOnDiscountAmount(
            useCartStore().plan,
            addOnId,
            useCartStore().annualUserCount
          );
          return totalDiscount;
        },
        0
      );
    },
    minimumPlanSpend(): number {
      return Number(
        useOnceStore().getPlanMinimumSpend(
          useCartStore().plan || useOnceStore().plan
        )
      );
    },
    minimumPlanSpendRemaining(): number {
      return this.minimumPlanSpend - this.planWithActiveUserPrice;
    },
    taxRate(): number {
      // onceStore.taxRate is a string ("0.1"); coerce so callers that add rather
      // than multiply (e.g. `1 + taxRate`) don't string-concatenate.
      return Number(useOnceStore().taxRate);
    },
    taxDetailsLabel(): string | null {
      return useOnceStore().getTaxDetailsLabel(useCartStore().plan);
    },
    estimatedMonthlyCostAfterDiscount(): number {
      // monthlyTotalAfterDiscount already nets the bundle saving, so both
      // branches are post-discount here.
      return Math.max(
        0,
        !useCartStore().hasPlanAttachedTax
          ? this.monthlyTotalAfterDiscount
          : this.monthlyTotalIncTax
      );
    },
    estimatedAnnualCostAfterDiscount(): number {
      // Purchasing standalone add-on
      if (useCheckoutStore().isAddOnPurchaseStep) {
        return this.annualAddOnsTotalAfterDiscount + this.annualAddOnsTotalTax;
      }

      const annualTotal = !useCartStore().hasPlanAttachedTax
        ? this.annualTotalAfterDiscount
        : this.annualTotalAfterDiscountIncTax;

      // Base plan only
      const total = !this.pricedAnnualAddOns.length
        ? annualTotal
        : // Base plan with annual add-ons
          annualTotal +
          this.annualAddOnsTotalAfterDiscount +
          this.annualAddOnsTotalTax;

      // The bundle saving is already netted into annualTotalAfterDiscount and
      // annualAddOnsTotalAfterDiscount, so no further deduction is needed here.
      return Math.max(0, total);
    },
    estimatedAnnualCost(): number {
      const hasPlanAttachedTax = useCartStore().hasPlanAttachedTax;
      const annualAddOnsTax = hasPlanAttachedTax
        ? this.annualAddOnsTotal * this.taxRate
        : 0;

      // Purchasing standalone add-on
      if (useCheckoutStore().isAddOnPurchaseStep) {
        return this.annualAddOnsTotal + annualAddOnsTax;
      }

      const annualTotal = !hasPlanAttachedTax
        ? this.annualTotalExtTax
        : this.annualTotalExtTax + this.annualTotalExtTax * this.taxRate;

      // Only deduct on the schedule the bundle belongs to (mirror of the guards
      // in the *AfterDiscount getters): under a monthly base, annualTotalExtTax
      // is 0 and this section holds only non-bundle annual add-ons, which must
      // not absorb the monthly bundle's saving.
      //
      // Tax is charged on the post-discount price, so the saving also removes the
      // tax that would have applied to the discounted portion (GROWTH-3472 item 2).
      const bundleSaving = useCartStore().isAnnual ? this.bundleSavingsTotal : 0;
      const bundleDeduction = hasPlanAttachedTax
        ? bundleSaving * (1 + this.taxRate)
        : bundleSaving;

      // Base plan only
      if (!this.pricedAnnualAddOns.length) {
        return Math.max(0, annualTotal - bundleDeduction);
      }

      // Base plan with annual add-ons
      return Math.max(
        0,
        annualTotal + this.annualAddOnsTotal + annualAddOnsTax - bundleDeduction
      );
    },

    annualAddOnsTotalTax(): number {
      if (!useCartStore().hasPlanAttachedTax) {
        return 0;
      }
      return this.annualAddOnsTotalAfterDiscount * useOnceStore().taxRate;
    },
    userCount(): number {
      return useCartStore().canPurchaseUsers
        ? useCartStore().annualUserCount
        : useOnceStore().totalEmployeeCount;
    },
    addOnUserCount() {
      return (addOnId = ''): number => {
        // For US Payroll (dynamic pricing), source the user count from the
        // payroll quote's recurring quantity rather than tenant/cart state.
        if (useOnceStore().isUsPayrollDynamicPricingEnabled(addOnId)) {
          const quote = usePricingStore().payrollQuote as {
            recurring?: Array<{ identifier: string; quantity?: number }>;
          } | null;
          const baseItem = quote?.recurring?.find(
            item => item.identifier === 'payroll-base'
          );
          if (baseItem?.quantity != null) {
            return baseItem.quantity;
          }
        }

        if (
          useCheckoutStore().isSelectingPlanType &&
          (useCartStore().isAnnualAddOn(addOnId) || addOnId === '')
        ) {
          return useCartStore().annualUserCount;
        } else if (
          useCheckoutStore().isAddOnPurchaseStep &&
          useCartStore().isAnnualAddOn(addOnId)
        ) {
          return useOnceStore().getPrePurchasedUserCountFromPlan();
        } else {
          return useOnceStore().totalEmployeeCount;
        }
      };
    },
    planDiscount(): PromoCodeDiscount | undefined {
      return this.promoDiscountMap[useCartStore().plan];
    },
    getTrialEndDate(): DateTime {
      const todayLocalTime = DateTime.local();
      const businessCreationDate = DateTime.fromISO(useOnceStore().createdDate);

      // The remaining trial date should always take into account the full potential length of the trial (31 days) with gamification completed.
      const trialEndDate = businessCreationDate.plus({
        days: TRIAL_LENGTH_DAYS,
      });

      if (trialEndDate < todayLocalTime) {
        return todayLocalTime;
      }

      return trialEndDate;
    },
    /**
     * Calculates the prorated amount for add-ons including tax and discounts
     * Returns 0 for monthly and annual paid monthly billing
     * Returns prorated amount for true annual billing
     *
     * @param {string} addOnId - The add-on ID
     * @return {number} The prorated amount
     */
    addOnDueTodayWithTax(): number {
      // Return 0 for monthly billing
      if (useCartStore().isMonthly) {
        return 0;
      }

      if (useCartStore().isAnnualPaidMonthly) {
        return this.monthlyPaymentAmount;
      }

      if (useCheckoutStore().isSubscriptionStep) {
        return this.annualAddOnsTotalAfterDiscount;
      }

      const addOns = this.pricedAnnualAddOns;
      if (!addOns?.length) {
        return this.estimatedAnnualCostAfterDiscount;
      }
      return addOns.reduce((total, addOnId) => {
        return total + this.calculateAddOnProratedAmount(addOnId);
      }, 0);
    },
    /**
     * Gets the amount due today based on the current checkout state
     *
     * @return {number} The amount due today
     */
    getAmountDueToday(): number {
      // For annual upgrade scenario, use net charge from API
      if (this.annualUpgradePricing) {
        return this.annualUpgradePricing.netChargeToday;
      }

      // Adding the bundle mid-term on APM charges nothing today: the apply
      // order only bills upfront for a true annual add-on (BundleOrderPlanner),
      // and the new instalment starts on the next billing date.
      if (
        useCheckoutStore().isAioApplyBundlePreviewScenario &&
        usePlansStore().isAnnualPaidMonthly(useOnceStore().plan)
      ) {
        return 0;
      }

      if (this.isMidContractApmToApmUpgrade) {
        return 0;
      }

      // For US Payroll (dynamic pricing), due-today equals the sum of the
      // one-time fees from the payroll quote. The 401k integration setup fee
      // is conditional: include it only when the 401k integration optional
      // service has been added to the cart.
      //
      // Silent fallback: if the dynamic quote isn't loaded (fetch failure or
      // still in flight), fall through to the legacy `addOnDueTodayWithTax`
      // path below — using `DP.price_wizard`-backed pricing rather than
      // returning 0 / showing an error state.
      const onceStore = useOnceStore();
      const cartStore = useCartStore();
      const isUsPayrollInCart = cartStore.addOns.some((addOnId: AddOnId) =>
        onceStore.isUsPayrollDynamicPricingEnabled(addOnId)
      );
      const payrollQuote = usePricingStore().payrollQuote as {
        oneTime?: Array<{
          identifier: string;
          amount: string;
          taxAmount?: string;
        }>;
      } | null;
      if (isUsPayrollInCart && payrollQuote) {
        const oneTimeItems = payrollQuote.oneTime ?? [];
        return oneTimeItems
          .filter(item =>
            item.identifier === '401k_integration_setup_fee'
              ? cartStore.isOptionalServiceInCart('401k-integration')
              : true
          )
          .reduce(
            (sum, item) =>
              sum + Number(item.amount) + Number(item.taxAmount ?? 0),
            0
          );
      }

      if (useCheckoutStore().isSelectingPlanType) {
        if (useCartStore().isAnnualPaidMonthly) {
          return this.monthlyPaymentAmount;
        }
        return this.estimatedAnnualCostAfterDiscount;
      }

      return this.addOnDueTodayWithTax;
    },
    freeMonthsAddOnMonthlyDeduction(): number {
      const quoteStore = useQuoteStore();
      let deduction = 0;

      for (const addOnId of this.pricedAnnualAddOns) {
        const discount = this.promoDiscountMap[addOnId];
        if (!discount?.freeMonths) {
          continue;
        }
        // If the discount has free months, we do not display it in the monthly payments
        const addOnCostAfterDiscount =
          quoteStore.addOnCostAfterDiscount(addOnId);
        const addOnTaxCost = quoteStore.getTaxCostValue(addOnId);
        deduction += (addOnCostAfterDiscount + addOnTaxCost) / 12;
      }

      return deduction;
    },
    monthlyPaymentAmount(): number {
      return (
        this.estimatedAnnualCostAfterDiscount / 12 -
        this.freeMonthsAddOnMonthlyDeduction
      );
    },
    annualBilledMonthlyPaymentAmount(): number {
      return this.estimatedAnnualCost / 12;
    },
    /**
     * Detects if this is a mid-contract upgrade from APM to APM
     * For APM to APM upgrades, customers are only charged on the 1st of the next month,
     * so the due today amount should be $0
     *
     * @return {boolean} True if this is a mid-contract APM to APM upgrade
     */
    isMidContractApmToApmUpgrade(): boolean {
      const onceStore = useOnceStore();
      const cartStore = useCartStore();
      const checkoutStore = useCheckoutStore();
      const plansStore = usePlansStore();

      // Only applies to plan selection flow
      if (!checkoutStore.isSelectingPlanType) {
        return false;
      }

      // Must be a change plan flow (not initial subscription)
      if (!checkoutStore.isChangePlanFlow) {
        return false;
      }

      // Must not be within the annual renewal period (mid-contract upgrade)
      if (onceStore.isAnnualSubscriptionEndingWithinNext31Days()) {
        return false;
      }

      const currentPlan = onceStore.plan;
      const targetPlan = cartStore.plan;

      // Both current and target plans must be APM (Annual Paid Monthly)
      const isCurrentPlanApm = plansStore.isAnnualPaidMonthly(currentPlan);
      const isTargetPlanApm = plansStore.isAnnualPaidMonthly(targetPlan);

      // Must be upgrading to a higher tier
      const isUpgradeToHigherTier = plansStore.isHigherTierPlan(
        currentPlan,
        targetPlan
      );

      return isCurrentPlanApm && isTargetPlanApm && isUpgradeToHigherTier;
    },
    calculateAddOnProratedAmount() {
      return (addOnId: string): number => {
        const userCount = this.addOnUserCount(addOnId);
        const proRatedPrice = useOnceStore().getAnnualAddOnProRataUpfrontPrice(
          addOnId,
          userCount
        );
        const taxRate = useOnceStore().taxRate;
        const taxAmount = proRatedPrice * taxRate;

        const { finalPrice, finalTaxAmount } = this.applyAddOnDiscount(
          addOnId,
          proRatedPrice,
          taxAmount
        );

        return (
          finalPrice + (useCartStore().hasPlanAttachedTax ? finalTaxAmount : 0)
        );
      };
    },
    applyAddOnDiscount() {
      return (
        addOnId: string,
        proRatedPrice: number,
        taxAmount: number
      ): { finalPrice: number; finalTaxAmount: number } => {
        const discount = this.getAddOnDiscount(addOnId);
        if (!discount) {
          return { finalPrice: proRatedPrice, finalTaxAmount: taxAmount };
        }

        if (discount.percentage) {
          return this.applyPercentageDiscount(
            proRatedPrice,
            taxAmount,
            discount.percentage
          );
        }

        if (discount.amount) {
          return this.applyAmountDiscount(
            addOnId,
            proRatedPrice,
            taxAmount,
            discount.amount
          );
        }

        return { finalPrice: proRatedPrice, finalTaxAmount: taxAmount };
      };
    },
    applyPercentageDiscount() {
      return (
        price: number,
        tax: number,
        percentage: number
      ): { finalPrice: number; finalTaxAmount: number } => {
        return {
          finalPrice: price * (1 - percentage),
          finalTaxAmount: tax * (1 - percentage),
        };
      };
    },
    applyAmountDiscount() {
      return (
        addOnId: string,
        price: number,
        tax: number,
        discountAmount: number
      ): { finalPrice: number; finalTaxAmount: number } => {
        const priceDiscount = Math.min(discountAmount, price);
        const taxDiscount = (priceDiscount / price) * tax;

        return {
          finalPrice: price - priceDiscount,
          finalTaxAmount: tax - taxDiscount,
        };
      };
    },
    getAddOnDiscount(): (addOnId: AddOnId) => PromoCodeDiscount | undefined {
      return (addOnId: AddOnId) => {
        return this.promoDiscountMap[addOnId];
      };
    },
    // Users the bundle discount applies across: pre-paid count on annual,
    // active employees on monthly. Deliberately ungated — only ever read
    // from a gated caller (bundleDiscountAmount); do not read it directly.
    bundleUserCount(): number {
      return useCartStore().isAnnual
        ? useCartStore().annualUserCount
        : useOnceStore().totalEmployeeCount;
    },
    // On every step of the bundle checkout, not just the plan step, so the
    // totals don't change between steps.
    hasAioExistingUserPromo(): boolean {
      return (
        useCheckoutStore().isAioBundleFlow &&
        getAioExistingUserPromoBillingTerm() !== null
      );
    },
    // A new annual term: a monthly tenant moving to APM or annual upfront, or
    // an annual tenant inside the renewal window, where the bundle starts a new
    // contract. The promo covers its whole first year rather than running to
    // the current term end.
    isAioExistingPromoNewAnnualTerm(): boolean {
      if (!this.hasAioExistingUserPromo || useCartStore().isMonthly) {
        return false;
      }
      return (
        getAioExistingUserPromoBillingTerm() === 'monthly' ||
        useOnceStore().isAnnualSubscriptionEndingWithinNext31Days()
      );
    },
    // The existing-customer promo replaces the standard bundle rate.
    bundleDiscountRate(): number {
      return this.hasAioExistingUserPromo
        ? AIO_EXISTING_USERS_PROMO_RATE
        : AIO_BUNDLE_DISCOUNT_RATE;
    },
    // Bundle discount is single-sourced here so no component hardcodes it.
    // Gated on the AIO flow so it can never alter the standard checkout.
    bundleDiscountAmount(): (id: number | string) => number {
      return (id: number | string): number => {
        if (!useCheckoutStore().isAioBundleFlow) {
          return 0;
        }
        return usePricingStore().getBundleDiscountAmount(
          useOnceStore().country,
          useCartStore().plan,
          id,
          this.bundleUserCount,
          this.bundleDiscountRate
        );
      };
    },
    bundleSavingsTotal(): number {
      if (!useCheckoutStore().isAioBundleFlow) {
        return 0;
      }
      const cart = useCartStore();
      return (
        this.planBundleDiscount +
        cart.addOns.reduce<number>(
          (sum, id) => sum + this.bundleDiscountAmount(id),
          0
        )
      );
    },
    // The plan's share of the bundle discount. Splitting the saving into a plan
    // and an add-on portion lets each taxable base be reduced by its own share,
    // so tax is charged on the post-discount price (GROWTH-3472 item 2).
    planBundleDiscount(): number {
      if (!useCheckoutStore().isAioBundleFlow) {
        return 0;
      }
      const cart = useCartStore();
      // A monthly bundle sitting on the minimum-spend floor discounts the floor,
      // not the per-user list price — the floor is what's actually billed
      // (GROWTH-3472 item 6).
      if (cart.isMonthly && this.minimumPlanSpendRemaining > 0) {
        return this.minimumPlanSpend * this.bundleDiscountRate;
      }
      return this.bundleDiscountAmount(cart.plan);
    },
    addOnsBundleDiscount(): number {
      return Math.max(0, this.bundleSavingsTotal - this.planBundleDiscount);
    },
    // Headline saving: the bundle discount only (15% on base + HR + Payroll).
    // Deliberately excludes the annual-vs-monthly difference and any
    // Messaging+/Analytics+ annual savings or promos.
    bundleHeadlineSaving(): number {
      if (!useCheckoutStore().isAioBundleFlow) {
        return 0;
      }
      return this.bundleSavingsTotal;
    },
    // The promo starts with the bundle: on an annual cart that's the contract
    // effective date, the 1st of the month after the base plan trial while it
    // runs; otherwise the start of next month.
    aioExistingPromoStart(): DateTime {
      const cart = useCartStore();
      return cart.isMonthly
        ? useQuoteStore().firstOfNextMonth()
        : useQuoteStore().getContractEffectiveDate(cart.plan);
    },
    // The existing-customer promo runs from aioExistingPromoStart to
    // aioExistingPromoEndDate, so on an annual term the savings headline covers
    // only the whole months in that window, not a full year. The discount line
    // keeps the full-year figure, like the base plan price.
    aioExistingPromoHeadlineSaving(): number {
      const annualSaving = this.bundleHeadlineSaving;
      const promoEnd = this.aioExistingPromoEndDate;
      if (!promoEnd || this.isAioExistingPromoNewAnnualTerm) {
        return annualSaving;
      }
      const monthsInTerm = 12;
      const monthsLeft = Math.ceil(
        DateTime.fromISO(promoEnd)
          .plus({ days: 1 })
          .diff(this.aioExistingPromoStart, 'months').months
      );
      const monthsCovered = Math.min(monthsInTerm, Math.max(0, monthsLeft));
      return (annualSaving / monthsInTerm) * monthsCovered;
    },
    // ISO date of the last day the existing-customer promo applies: 12 months
    // from aioExistingPromoStart, or the current annual term, if that ends
    // sooner. The term end date is the first day of the renewed term. A new
    // annual term starts with the promo, so its promo runs the full 12 months.
    aioExistingPromoEndDate(): string | null {
      if (!this.hasAioExistingUserPromo) {
        return null;
      }
      const yearEnd = this.aioExistingPromoStart.plus({ months: 12 });
      // Inside the renewal window the new contract starts at the current term
      // end, on annual (the term renews) and on monthly (the switch lands
      // there), so the promo runs for 12 months from it.
      const renewedTermEnd = this.renewedAnnualTermEnd();
      if (renewedTermEnd) {
        return renewedTermEnd.minus({ days: 1 }).toISODate();
      }
      if (this.isAioExistingPromoNewAnnualTerm) {
        return yearEnd.minus({ days: 1 }).toISODate();
      }
      const termEndDate = useOnceStore().getAnnualSubscriptionEndDate();
      const termEnd =
        !useCartStore().isMonthly && termEndDate
          ? DateTime.fromISO(termEndDate)
          : yearEnd;
      return DateTime.min(termEnd, yearEnd).minus({ days: 1 }).toISODate();
    },
  },
  actions: {
    calculateCost(value: number, count: number): number {
      return Number(value) * count;
    },
    getAddOnPrice(addOnId: string): number {
      const isAnnualAddOn = useAddOnStore().isAnnualAddOn(addOnId);

      if (isAnnualAddOn) {
        return (
          useOnceStore().getAddOnPrice(addOnId, useCartStore().plan) *
          this.addOnUserCount(addOnId) *
          12
        );
      }

      // If the user has active user billing, it means it's a weekly billing,
      // so we multiply the price by 4 to show monthly total
      if (useCartStore().hasActiveUserBilling) {
        return (
          useOnceStore().getAddOnPrice(addOnId, useCartStore().plan) *
          this.addOnUserCount(addOnId) *
          4
        );
      }

      return (
        (useOnceStore().getAddOnPricing(addOnId, useCartStore().plan)?.pricing
          ?.price ?? 0) * this.addOnUserCount(addOnId)
      );
    },
    addOnCostAfterDiscount(addOnId: AddOnId): number {
      const addOnPrice = this.getAddOnPrice(addOnId);
      // getAddOnPrice returns an annual amount for annual add-ons and a monthly
      // amount for monthly add-ons, so the discount must be computed in the same
      // period to apply the freeMonths proration correctly.
      const isAnnualTotal = useAddOnStore().isAnnualAddOn(addOnId);
      return (
        addOnPrice -
        this.addOnPromoDiscountAmount(addOnPrice, addOnId, isAnnualTotal)
      );
    },
    getTaxCostValue(addOnId: AddOnId): number {
      if (!this.taxRate) {
        return 0;
      }

      // Tax the post-bundle-discount amount so a bundled HR/Payroll row's GST
      // matches the discounted price (GROWTH-3472 item 2). bundleDiscountAmount
      // is 0 outside the AIO flow / for non-bundled add-ons, so standard
      // checkout is unaffected.
      const value = Math.max(
        0,
        this.addOnCostAfterDiscount(addOnId) - this.bundleDiscountAmount(addOnId)
      );

      return value * this.taxRate;
    },
    getAnnualDiscountPercentage(annualPlan: SubscriptionPlan): number {
      return useOnceStore().getAnnualDiscountPercentage(annualPlan);
    },
    formatDate(date: DateTime): string {
      const formattedDate = date
        .setLocale(useOnceStore().locale)
        .toLocaleString({ year: 'numeric', month: 'short', day: 'numeric' });

      return formattedDate || '';
    },
    getPaymentDate(isMonthly: boolean): DateTime {
      const discount = this.planDiscount;

      const todayLocalTime = DateTime.local();
      if (!isMonthly) {
        if (discount?.freeMonths) {
          todayLocalTime.plus({ months: discount.freeMonths });
        }
        return todayLocalTime;
      }

      let paymentDueDate = this.getTrialEndDate;

      // Until we have promo code support for add ons, skip free month calculations if add ons in cart
      if (discount?.freeMonths && !useCartStore().hasAddOns) {
        paymentDueDate = paymentDueDate.plus({ months: discount.freeMonths });
      }

      return paymentDueDate;
    },
    getContractEffectiveDate(targetPlan: number): DateTime {
      const plansStore = usePlansStore();
      if (plansStore.isMonthly(targetPlan)) {
        return this.firstOfMonth();
      }

      const { trialEndedAt, plan: currentPlanId } = useOnceStore();
      const trialEndsAt = trialEndedAt ? DateTime.fromISO(trialEndedAt) : null;

      // if converting from a FREE plan
      if (!currentPlanId || currentPlanId === SUBSCRIPTION.FREE) {
        return this.firstOfNextMonth();
      }

      if (trialEndsAt && trialEndsAt > DateTime.local()) {
        return this.firstOfNextMonth(trialEndsAt);
      } else {
        return this.firstOfNextMonth();
      }
    },
    firstOfMonth(): DateTime {
      return DateTime.local().startOf('month');
    },
    firstOfNextMonth(referenceDate?: DateTime): DateTime {
      const date = referenceDate ?? DateTime.local();
      return date.plus({ months: 1 }).startOf('month');
    },
    getInvoiceDate(isMonthly: boolean): DateTime {
      const discount = this.planDiscount;

      let invoiceDate = this.getTrialEndDate;

      if (isMonthly) {
        invoiceDate = invoiceDate.endOf('month');
      }

      // Until we have promo code support for add-ons, skip free month calculations if add-ons in cart
      if (discount?.freeMonths && !useCartStore().hasAddOns) {
        invoiceDate = invoiceDate.plus({ months: discount.freeMonths });
      }

      return invoiceDate;
    },
    getRenewalDate(isMonthly: boolean): DateTime {
      if (isMonthly) {
        return this.getInvoiceDate(true);
      }

      if (!useCheckoutStore().isAddOnPurchaseStep) {
        const targetPlan = useCartStore().plan;
        const currentTermEnd = this.midTermAnnualTermEnd(targetPlan);
        if (currentTermEnd) {
          return currentTermEnd;
        }
        const isAioRenewalJourney =
          useCheckoutStore().isAioBundleChangePlanFlow &&
          usePlansStore().isAnnual(targetPlan);
        const renewedTermEnd = isAioRenewalJourney
          ? this.renewedAnnualTermEnd()
          : null;
        if (renewedTermEnd) {
          return renewedTermEnd;
        }
        const contractEffectiveDate = this.getContractEffectiveDate(targetPlan);
        return contractEffectiveDate.plus({ years: 1 });
      }

      return DateTime.fromISO(useOnceStore().getAnnualSubscriptionEndDate());
    },
    // A tenant already on annual or APM who changes plan mid-term keeps their
    // term, so it still renews when that term ends. Inside the renewal window
    // the change renews the term instead (see renewedAnnualTermEnd), and a
    // monthly tenant starts a new annual term; both return null.
    midTermAnnualTermEnd(targetPlan: number): DateTime | null {
      const plansStore = usePlansStore();
      const onceStore = useOnceStore();
      if (
        !plansStore.isAnnual(onceStore.plan) ||
        !plansStore.isAnnual(targetPlan) ||
        onceStore.isAnnualSubscriptionEndingWithinNext31Days()
      ) {
        return null;
      }
      const termEnd = DateTime.fromISO(
        onceStore.getAnnualSubscriptionEndDate() ?? ''
      );
      return termEnd.isValid ? termEnd : null;
    },
    // Inside the renewal window, an annual tenant's change renews the term for
    // another 12 months from the current term end, so that's when the new
    // term ends. Null outside the window, or when the term end is unknown.
    renewedAnnualTermEnd(): DateTime | null {
      const onceStore = useOnceStore();
      if (
        !usePlansStore().isAnnual(onceStore.plan) ||
        !onceStore.isAnnualSubscriptionEndingWithinNext31Days()
      ) {
        return null;
      }
      const termEnd = DateTime.fromISO(
        onceStore.getAnnualSubscriptionEndDate() ?? ''
      );
      return termEnd.isValid ? termEnd.plus({ years: 1 }) : null;
    },
    _promoDiscountAmount(totalExtTax: number, isAnnualTotal: boolean): number {
      if (this.planDiscount?.percentage) {
        const freeMonths = this.planDiscount.freeMonths;
        if (isAnnualTotal && freeMonths) {
          return totalExtTax * this.planDiscount.percentage * (freeMonths / 12);
        }
        return totalExtTax * this.planDiscount.percentage;
      }

      if (this.planDiscount?.amount) {
        return totalExtTax - this.planDiscount.amount;
      }

      return 0;
    },
    addOnPromoDiscountAmount(
      totalExtTax: number,
      addOnId: AddOnId,
      isAnnualTotal: boolean
    ): number {
      const discount = this.promoDiscountMap[addOnId];

      if (discount?.percentage) {
        const freeMonths = discount.freeMonths;
        if (isAnnualTotal && freeMonths) {
          return totalExtTax * discount.percentage * (freeMonths / 12);
        }
        return totalExtTax * discount.percentage;
      }

      if (discount?.amount) {
        return totalExtTax - discount.amount;
      }

      return 0;
    },
    _checkDiscountExists(value: SubscriptionPlan | AddOnId): boolean {
      return Boolean(this.promoDiscountMap[value]);
    },
    addDiscount(discount: PromoCodeDiscount): void {
      if (discount?.planId) {
        const planDiscountExists = this._checkDiscountExists(discount.planId);
        this.promoDiscountMap[discount.planId] = discount;

        if (!planDiscountExists) {
          this.promoDiscounts.push(discount.planId);
        }
        return;
      }

      if (discount?.addOnId) {
        const addOnDiscountExists = this._checkDiscountExists(discount.addOnId);
        this.promoDiscountMap[discount.addOnId] = discount;

        if (!addOnDiscountExists) {
          this.promoDiscounts.push(discount.addOnId);
        }
        return;
      }
    },
    _removeDiscountByField(value: SubscriptionPlan | AddOnId): void {
      // Clear the auto-applied provenance for the removed code: once a code is
      // removed, re-adding it (e.g. the user typing it back in) counts as
      // user-input, not auto-applied.
      const removedCode = this.promoDiscountMap[value]?.code?.toUpperCase();
      if (removedCode) {
        this.autoAppliedCodes = this.autoAppliedCodes.filter(
          code => code !== removedCode
        );
      }

      delete this.promoDiscountMap[value];
      this.promoDiscounts = this.promoDiscounts.filter(
        (existingDiscount: SubscriptionPlan | AddOnId) =>
          existingDiscount !== value
      );
    },
    removeDiscount(
      discount: Pick<PromoCodeDiscount, 'planId' | 'addOnId'>
    ): void {
      if (discount?.planId) {
        this._removeDiscountByField(discount.planId);
      }

      if (discount?.addOnId) {
        this._removeDiscountByField(discount.addOnId);
      }
    },
    clearAllPromoDiscounts(): void {
      this.promoDiscounts = [];
      this.promoDiscountMap = {};
      this.autoAppliedCodes = [];
      this.promoCodeError = '';
    },
    updatePromoCodeError(error: string): void {
      this.promoCodeError = error;
    },
    markCodeAutoApplied(code: string): void {
      const normalized = code?.toUpperCase();
      if (normalized && !this.autoAppliedCodes.includes(normalized)) {
        this.autoAppliedCodes.push(normalized);
      }
    },
    async fetchAnnualUpgradePricing({
      isAioBundle = false,
    }: { isAioBundle?: boolean } = {}): Promise<void> {
      const onceStore = useOnceStore();
      const cartStore = useCartStore();

      this.annualUpgradePricingRequestId += 1;
      const requestId = this.annualUpgradePricingRequestId;
      const isSuperseded = (): boolean =>
        requestId !== this.annualUpgradePricingRequestId;

      this.isLoadingAnnualUpgradePricing = true;
      this.annualUpgradePricing = null;
      this.aioApmInstalmentPricing = null;
      this.annualUpgradePricingError = '';

      try {
        const hostname = onceStore.hostname;
        // The AIO preview prices adding the bundle to the plan the tenant
        // already holds; the backend only takes that branch when selectedPlan
        // is their current edition.
        const planId = isAioBundle ? onceStore.plan : cartStore.plan;

        // While getPrePurchasedUserCountFromPlan is computed and reactive, we need the final
        // correct amount that is only guaranteed if subscription data is loaded.
        // loadBusiness should have already triggered the load, we just await it here.
        await onceStore.ensureSubscriptionDataLoaded();

        const prePurchasedCount = onceStore.getPrePurchasedUserCountFromPlan();

        const response = await mushodoPromise('DoAnnualCalculation', {
          Hostname: hostname,
          selectedPlan: planId,
          EMPLOYEE_QUANTITY_PREPURCHASE: prePurchasedCount,
          IsPurchaseAdditionalUser: false,
          IsMidContractUpgrade: !isAioBundle,
          ...(isAioBundle && { isAioBundle: true }),
        });

        if (isSuperseded()) {
          return;
        }

        if (response.error) {
          console.error(
            'Failed to load annual upgrade pricing:',
            response.error
          );
          this.annualUpgradePricingError =
            'error-previewing-subscription-costs';
          return;
        }

        if (isAioBundle) {
          // A backend without the AIO preview ignores
          // isAioBundle and returns a full-year quote with no marker; showing
          // it would put a non-prorated figure in Due today.
          if (response.isAioBundle !== true) {
            console.error('Unexpected AIO bundle preview response:', response);
            this.annualUpgradePricingError =
              'error-previewing-subscription-costs';
            return;
          }
          // APM previews one representative instalment, not a prorated charge.
          if (response.isAnnualPaidMonthly) {
            this.aioApmInstalmentPricing = {
              price: response.price,
              priceIncTax: response.priceIncTax,
            };
            return;
          }
        }

        this.annualUpgradePricing = {
          proratedAmount: response.proratedAmount,
          priceIncTax: response.priceIncTax,
          previousPlanCredit: response.previousPlanCredit,
          netChargeToday: response.netChargeToday,
          currency: response.currency,
          addTax: response.addTax,
        };
      } catch (error) {
        if (isSuperseded()) {
          return;
        }
        console.error('Failed to load annual upgrade pricing:', error);
        this.annualUpgradePricingError = 'error-previewing-subscription-costs';
      } finally {
        if (!isSuperseded()) {
          this.isLoadingAnnualUpgradePricing = false;
        }
      }
    },
    resetAnnualUpgradePricing(): void {
      this.annualUpgradePricingRequestId += 1;
      this.annualUpgradePricing = null;
      this.aioApmInstalmentPricing = null;
      this.isLoadingAnnualUpgradePricing = false;
      this.annualUpgradePricingError = '';
    },
  },
});
